import { GameMap } from './Map';
import { Camera, Position, TerrainType, Tile, Unit, FactionType, CityLevel } from './types';
import { UNIT_DEFINITIONS } from './unitDefinitions';
import { VisionSystem } from './VisionSystem';
import { debug } from './debug';

export class Renderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private tileSize: number = 64;
  private spriteMode: 'outline' | 'coloured' | 'filled' | 'silhouette' | 'borderless' | 'coloured-borderless' | 'coloured-faction' = 'outline';
  private isMobile: boolean = false;
  private devicePixelRatio: number = 1;
  private terrainColors: Record<TerrainType, string> = {
    // Urban (special)
    [TerrainType.URBAN]: '#9B9B9B',
    
    // Normandy Campaign
    [TerrainType.OPEN_FIELDS]: '#9DB668',      // Field green
    [TerrainType.BOCAGE]: '#6B7F47',           // Hedgerow green  
    [TerrainType.DENSE_WOODS]: '#445236',      // Forest green
    
    // Tunisia Campaign
    [TerrainType.DESERT_PLAINS]: '#E2D4A7',    // Light sand
    [TerrainType.ROCKY_HIGHLANDS]: '#B89968',  // Rocky tan
    [TerrainType.SHIFTING_DUNES]: '#9B7653',   // Deep sand
    
    // Stalingrad Campaign
    [TerrainType.FROZEN_STEPPE]: '#D0D0D0',    // Darker gray for more contrast
    [TerrainType.SNOW_DRIFTS]: '#FFFFFF',      // Snow white
    [TerrainType.FROZEN_MARSHES]: '#A8B5C1',   // Ice gray-blue
    
    // Okinawa Campaign
    [TerrainType.RICE_PADDIES]: '#8FA563',     // Rice field green
    [TerrainType.CORAL_RIDGES]: '#D4C8A8',     // Limestone tan
    [TerrainType.DENSE_JUNGLE]: '#4A5D3A',     // Jungle green
    
    // Campobasso Campaign
    [TerrainType.VALLEY_FLOORS]: '#A4B576',    // Valley grass green
    [TerrainType.WOODED_HILLS]: '#8B7355',     // Autumn brown
    [TerrainType.MOUNTAIN_RIDGES]: '#6B5D4F',  // Mountain gray-brown
    
    // Battle of France Campaign
    [TerrainType.FRENCH_COUNTRYSIDE]: '#B8C76F', // Light French green
    [TerrainType.ROLLING_HILLS]: '#8FA068',      // Hill green
    [TerrainType.DENSE_FORESTS]: '#556B3D',      // French forest green
    
    // Albania 1940 Campaign
    [TerrainType.COASTAL_LOWLANDS]: '#A8C090',   // Mediterranean coastal green
    [TerrainType.ALBANIAN_HIGHLANDS]: '#8B7D6B', // Highland brown-gray
    [TerrainType.PINDUS_MOUNTAINS]: '#6B5D54'    // Mountain dark brown
  };

  private flagImages: Record<string, HTMLImageElement> = {};
  private flagImagesLoaded: boolean = false;
  private categoryImages: Record<string, HTMLImageElement> = {};
  private filledCategoryImages: Record<string, HTMLImageElement> = {}; // Store filled versions
  private levelImages: Record<string, HTMLImageElement> = {};
  private unitIconsLoaded: boolean = false;
  private mineImage: HTMLImageElement | null = null;
  private filledMineImage: HTMLImageElement | null = null;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get 2D context');
    this.ctx = ctx;
    
    // Load flag images
    this.loadFlagImages();
    
    // Load unit icons
    this.loadUnitIcons();
    
    // Load mine icon
    this.loadMineIcon();
    
    this.resizeCanvas();
    window.addEventListener('resize', () => this.resizeCanvas());
  }

  private async loadFlagImages(): Promise<void> {
    const flagPaths = {
      'United States Of America': '/flags/USA.jpg',
      '3rd Reich': '/flags/Reich.jpg',
      'United Kingdom': '/flags/Brits.jpg',
      'USSR': '/flags/Soviet.jpg',
      'Imperial Japan': '/flags/Japs.jpg',
      'Canada': '/flags/Canadians.jpg',
      'Fascist Italy': '/flags/Italians.jpg',
      'France': '/flags/French.jpg',
      'Greece': '/flags/Greeks.jpg'
    };

    const loadPromises = Object.entries(flagPaths).map(([faction, path]) => {
      return new Promise<void>((resolve) => {
        const img = new Image();
        img.onload = () => {
          this.flagImages[faction] = img;
          resolve();
        };
        img.onerror = () => {
          // If image fails to load, create a colored rectangle as fallback
          const canvas = document.createElement('canvas');
          canvas.width = 32;
          canvas.height = 20;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            const fallbackColor = faction === 'United States Of America' ? '#4B5320' : 
                                 faction === '3rd Reich' ? '#708090' : 
                                 faction === 'United Kingdom' ? '#003F87' :
                                 faction === 'USSR' ? '#CC0000' : 
                                 faction === 'Imperial Japan' ? '#DC143C' :
                                 faction === 'Canada' ? '#D2691E' :
                                 faction === 'Fascist Italy' ? '#009246' :
                                 faction === 'France' ? '#7A9BB8' :
                                 faction === 'Greece' ? '#0072CE' : '#FFFFFF';
            ctx.fillStyle = fallbackColor;
            ctx.fillRect(0, 0, 32, 20);
            
            const fallbackImg = new Image();
            fallbackImg.src = canvas.toDataURL();
            this.flagImages[faction] = fallbackImg;
          }
          resolve();
        };
        img.src = path;
      });
    });

    // Create neutral flag (white rectangle)
    const neutralPromise = new Promise<void>((resolve) => {
      const canvas = document.createElement('canvas');
      canvas.width = 32;
      canvas.height = 20;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, 32, 20);
        ctx.strokeStyle = '#CCCCCC';
        ctx.lineWidth = 1;
        ctx.strokeRect(0, 0, 32, 20);
        
        const neutralImg = new Image();
        neutralImg.onload = () => {
          this.flagImages['neutral'] = neutralImg;
          resolve();
        };
        neutralImg.src = canvas.toDataURL();
      } else {
        resolve();
      }
    });

    await Promise.all([...loadPromises, neutralPromise]);
    this.flagImagesLoaded = true;
  }

  private async loadUnitIcons(): Promise<void> {
    // Helper function to get sprite path based on current mode
    const getSpritePath = (baseName: string) => {
      if (this.spriteMode === 'filled') return `/NEWIcons/${baseName}Filled.png`;
      if (this.spriteMode === 'coloured') return `/NEWIcons/${baseName}Coloured.png`;
      if (this.spriteMode === 'silhouette') return `/NEWIcons/${baseName}Silhouette.png`;
      if (this.spriteMode === 'borderless') return `/NEWIcons/${baseName}.png`; // Use outline icons for borderless
      if (this.spriteMode === 'coloured-borderless') return `/NEWIcons/${baseName}ColouredTint.png`; // Use ColouredTint icons
      if (this.spriteMode === 'coloured-faction') return `/NEWIcons/${baseName}ColouredTint.png`; // Use ColouredTint icons
      return `/NEWIcons/${baseName}.png`;
    };
    
    // Always load filled versions for borderless modes
    const getFilledPath = (baseName: string) => `/NEWIcons/${baseName}Filled.png`;
    
    const categoryPaths = {
      'infantry': getSpritePath('Infantry'),
      'armor': getSpritePath('Armour'),
      'artillery': getSpritePath('Artillery'),
      'reconnaissance': getSpritePath('Recon'),
      'aircraft': getSpritePath('Fighter'), // Keep for backwards compatibility - default to fighter
      'fighters': getSpritePath('Fighter'),
      'bombers': getSpritePath('Bomber'),
      'transport_aircraft': getSpritePath('TransportAircraft'),
      'engineers': getSpritePath('Engineer'),
      'medics': getSpritePath('Medic'),
      'transport': getSpritePath('Transport'),
      'halftrack': getSpritePath('HalfTrack'),
      'upgrading': getSpritePath('Upgrading')
    };

    const levelPaths = {
      '2': '/icons/Level2.png',
      '3': '/icons/Level3.png',
      '4': '/icons/Level4.png'
    };

    const categoryPromises = Object.entries(categoryPaths).map(([category, path]) => {
      return new Promise<void>((resolve) => {
        const img = new Image();
        img.onload = () => {
          this.categoryImages[category] = img;
          resolve();
        };
        img.onerror = () => {
          debug.warn(`Failed to load category icon: ${path}`);
          resolve();
        };
        img.src = path;
      });
    });

    const levelPromises = Object.entries(levelPaths).map(([level, path]) => {
      return new Promise<void>((resolve) => {
        const img = new Image();
        img.onload = () => {
          this.levelImages[level] = img;
          resolve();
        };
        img.onerror = () => {
          debug.warn(`Failed to load level icon: ${path}`);
          resolve();
        };
        img.src = path;
      });
    });

    // Also load filled versions for borderless modes
    const filledCategories = {
      'infantry': getFilledPath('Infantry'),
      'armor': getFilledPath('Armour'),
      'artillery': getFilledPath('Artillery'),
      'reconnaissance': getFilledPath('Recon'),
      'fighters': getFilledPath('Fighter'),
      'bombers': getFilledPath('Bomber'),
      'transport_aircraft': getFilledPath('TransportAircraft'),
      'engineers': getFilledPath('Engineer'),
      'medics': getFilledPath('Medic'),
      'transport': getFilledPath('Transport'),
      'halftrack': getFilledPath('HalfTrack'),
      'upgrading': getFilledPath('Upgrading')
    };
    
    const filledPromises = Object.entries(filledCategories).map(([category, path]) => {
      return new Promise<void>((resolve) => {
        const img = new Image();
        img.onload = () => {
          this.filledCategoryImages[category] = img;
          resolve();
        };
        img.onerror = () => {
          debug.warn(`Failed to load filled category icon: ${path}`);
          resolve();
        };
        img.src = path;
      });
    });
    
    await Promise.all([...categoryPromises, ...levelPromises, ...filledPromises]);
    this.unitIconsLoaded = true;
  }
  
  setSpriteMode(mode: 'outline' | 'coloured' | 'filled' | 'silhouette' | 'borderless' | 'coloured-borderless' | 'coloured-faction'): void {
    this.spriteMode = mode;
    debug.log(`Renderer sprite mode set to: ${mode}`);
    
    // Helper function to get sprite path based on mode
    const getSpritePath = (baseName: string) => {
      if (mode === 'filled') return `/NEWIcons/${baseName}Filled.png`;
      if (mode === 'coloured') return `/NEWIcons/${baseName}Coloured.png`;
      if (mode === 'silhouette') return `/NEWIcons/${baseName}Silhouette.png`;
      if (mode === 'borderless') return `/NEWIcons/${baseName}.png`; // Use outline icons for borderless
      if (mode === 'coloured-borderless') return `/NEWIcons/${baseName}ColouredTint.png`; // Use ColouredTint icons
      if (mode === 'coloured-faction') return `/NEWIcons/${baseName}ColouredTint.png`; // Use ColouredTint icons
      return `/NEWIcons/${baseName}.png`;
    };
    
    // Reload all unit icons with new sprite variants
    const unitsToReload = [
      { category: 'engineers', name: 'Engineer' },
      { category: 'infantry', name: 'Infantry' },
      { category: 'medics', name: 'Medic' },
      { category: 'armor', name: 'Armour' },
      { category: 'artillery', name: 'Artillery' },
      { category: 'reconnaissance', name: 'Recon' },
      { category: 'fighters', name: 'Fighter' },
      { category: 'bombers', name: 'Bomber' },
      { category: 'upgrading', name: 'Upgrading' },
      { category: 'transport_aircraft', name: 'TransportAircraft' },
      { category: 'transport', name: 'Transport' },
      { category: 'halftrack', name: 'HalfTrack' },
      { category: 'aircraft', name: 'Fighter' } // Backwards compatibility
    ];
    
    unitsToReload.forEach(unit => {
      const img = new Image();
      const path = getSpritePath(unit.name);
      img.onload = () => {
        this.categoryImages[unit.category] = img;
        debug.log(`${unit.name} sprite updated to: ${path}`);
      };
      img.onerror = () => {
        debug.warn(`Failed to load ${unit.name} sprite: ${path}`);
      };
      img.src = path;
    });
    
    // Also reload mine icon
    const mineImg = new Image();
    const minePath = getSpritePath('Mine');
    mineImg.onload = () => {
      this.mineImage = mineImg;
      debug.log(`Mine sprite updated to: ${minePath}`);
    };
    mineImg.onerror = () => {
      debug.warn(`Failed to load mine sprite: ${minePath}`);
    };
    // Add cache-busting timestamp to ensure fresh load
    mineImg.src = `${minePath}?t=${Date.now()}`;
  }

  private resizeCanvas(): void {
    // Detect mobile
    this.isMobile = window.innerWidth <= 812 || window.innerHeight <= 812;
    
    // Handle high DPI displays
    this.devicePixelRatio = window.devicePixelRatio || 1;
    
    // Set display size (css pixels)
    this.canvas.style.width = window.innerWidth + 'px';
    this.canvas.style.height = window.innerHeight + 'px';
    
    // Set actual size in memory (scaled for DPI)
    this.canvas.width = window.innerWidth * this.devicePixelRatio;
    this.canvas.height = window.innerHeight * this.devicePixelRatio;
    
    // Scale all drawing operations by the DPI ratio
    this.ctx.scale(this.devicePixelRatio, this.devicePixelRatio);
    
    // Enable better rendering for text
    this.ctx.imageSmoothingEnabled = true;
    this.ctx.imageSmoothingQuality = 'high';
  }

  render(map: GameMap, camera: Camera, selectedTile: Position | null, units: Unit[], selectedUnit: string | null, availableMoves: Position[] | null, enemiesInRange: Position[] | null = null, visionSystem?: VisionSystem, currentFaction?: FactionType, minePlacementTiles: Position[] | null = null, healTargetTiles: Position[] | null = null, boardingTargetTiles: Position[] | null = null): void {
    this.clear();
    
    // const tiles = map.getTiles();
    const visibleTiles = this.getVisibleTiles(map, camera);
    
    // Draw tiles
    for (const { tile, screenX, screenY } of visibleTiles) {
      this.drawTile(tile, screenX, screenY, camera.zoom);
    }
    
    // Draw city borders
    this.drawCityBorders(map, camera);
    
    // Draw available movement tiles
    if (availableMoves) {
      this.drawAvailableMoves(availableMoves, camera);
    }
    
    // Draw enemies in range
    if (enemiesInRange) {
      this.drawEnemiesInRange(enemiesInRange, camera);
    }
    
    // Draw mine placement tiles in yellow
    if (minePlacementTiles) {
      this.drawMinePlacementTiles(minePlacementTiles, camera);
    }
    
    // Draw heal/repair target tiles in green
    if (healTargetTiles) {
      this.drawHealTargetTiles(healTargetTiles, camera);
    }
    
    // Draw boarding target tiles
    if (boardingTargetTiles) {
      this.drawBoardingTargetTiles(boardingTargetTiles, camera);
    }
    
    // Draw mines first (only visible to owner faction) so units appear on top
    this.drawMines(map, camera, currentFaction, visionSystem);
    
    // Draw units on top of mines (only visible ones if fog of war is enabled)
    this.drawUnits(units, camera, selectedUnit, visionSystem, currentFaction);
    
    // Draw fog of war overlay
    if (visionSystem) {
      this.drawFogOfWar(visibleTiles, visionSystem, camera);
    }
    
    // Draw highlights and grid on top
    for (const { tile, screenX, screenY } of visibleTiles) {
      // Highlight selected tile
      if (selectedTile && tile.x === selectedTile.x && tile.y === selectedTile.y) {
        this.drawTileHighlight(screenX, screenY, camera.zoom);
      }
    }
    
    // Draw grid
    for (const { screenX, screenY } of visibleTiles) {
      this.drawTileOutline(screenX, screenY, camera.zoom);
    }
    
    // Draw city names on top of everything
    this.drawCityNames(map, camera);
    
  }

  private getVisibleTiles(map: GameMap, camera: Camera): Array<{tile: Tile, screenX: number, screenY: number}> {
    const visible = [];
    const tileSize = this.tileSize * camera.zoom;
    
    const startX = Math.floor(-camera.x / tileSize);
    const startY = Math.floor(-camera.y / tileSize);
    const endX = Math.ceil((this.canvas.width - camera.x) / tileSize);
    const endY = Math.ceil((this.canvas.height - camera.y) / tileSize);
    
    for (let y = startY; y < endY; y++) {
      for (let x = startX; x < endX; x++) {
        const tile = map.getTile(x, y);
        if (tile) {
          visible.push({
            tile,
            screenX: camera.x + x * tileSize,
            screenY: camera.y + y * tileSize
          });
        }
      }
    }
    
    return visible;
  }

  private clear(): void {
    this.ctx.fillStyle = '#1a1a1a';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
  }

  private drawTile(tile: Tile, x: number, y: number, zoom: number): void {
    const size = this.tileSize * zoom;
    
    // Use standard terrain color for all tiles
    const tileColor = this.terrainColors[tile.terrain] || '#9B9B9B'; // Fallback to gray
    
    this.ctx.fillStyle = tileColor;
    this.ctx.fillRect(x, y, size, size);
    
    
    // Draw city if present
    if (tile.city) {
      this.drawCity(tile.city, x, y, size, tile);
    }
  }
  
  private drawCity(city: any, x: number, y: number, size: number, tile: Tile): void {
    // Find center tile of the city (middle element of tiles array)
    const centerIndex = Math.floor(city.tiles.length / 2);
    const centerTile = city.tiles[centerIndex];
    
    if (tile.x === centerTile.x && tile.y === centerTile.y) {
      // Draw flag pole
      this.ctx.fillStyle = '#654321';
      this.ctx.fillRect(x + size * 0.45, y + size * 0.2, size * 0.1, size * 0.6);
      
      // Draw flag
      if (this.flagImagesLoaded) {
        const flagFaction = city.faction || 'neutral';
        const flagImage = this.flagImages[flagFaction];
        if (flagImage) {
          this.ctx.drawImage(
            flagImage,
            x + size * 0.55,
            y + size * 0.25,
            size * 0.3,
            size * 0.2
          );
        }
      } else {
        // Fallback to colored rectangle while images are loading
        const flagColor = city.faction === 'United States Of America' ? '#4B5320' : 
                         city.faction === '3rd Reich' ? '#708090' : 
                         city.faction === 'United Kingdom' ? '#003F87' :
                         city.faction === 'USSR' ? '#CC0000' :
                         city.faction === 'Imperial Japan' ? '#DC143C' :
                         city.faction === 'Canada' ? '#D2691E' :
                         city.faction === 'Fascist Italy' ? '#009246' :
                         city.faction === 'France' ? '#7A9BB8' :
                         city.faction === 'Greece' ? '#0072CE' :
                         '#FFFFFF';
        this.ctx.fillStyle = flagColor;
        this.ctx.fillRect(x + size * 0.55, y + size * 0.25, size * 0.3, size * 0.2);
      }
      
      // No separate capture indicator here - it's handled in city name drawing
    }
  }
  
  private drawAvailableMoves(moves: Position[], camera: Camera): void {
    const tileSize = this.tileSize * camera.zoom;
    
    this.ctx.fillStyle = 'rgba(100, 200, 100, 0.3)';
    this.ctx.strokeStyle = 'rgba(100, 200, 100, 0.8)';
    this.ctx.lineWidth = 2;
    
    for (const move of moves) {
      const x = camera.x + move.x * tileSize;
      const y = camera.y + move.y * tileSize;
      
      // Fill tile with transparent green
      this.ctx.fillRect(x, y, tileSize, tileSize);
      
      // Draw pulsing border
      const time = Date.now() / 1000;
      const pulse = Math.sin(time * 3) * 0.1 + 0.9;
      this.ctx.globalAlpha = pulse;
      this.ctx.strokeRect(x + 2, y + 2, tileSize - 4, tileSize - 4);
      this.ctx.globalAlpha = 1;
    }
  }

  private drawEnemiesInRange(enemies: Position[], camera: Camera): void {
    const tileSize = this.tileSize * camera.zoom;
    
    this.ctx.fillStyle = 'rgba(255, 50, 50, 0.3)';
    this.ctx.strokeStyle = 'rgba(255, 50, 50, 0.8)';
    this.ctx.lineWidth = 2;
    
    for (const enemy of enemies) {
      const x = camera.x + enemy.x * tileSize;
      const y = camera.y + enemy.y * tileSize;
      
      // Fill tile with transparent red
      this.ctx.fillRect(x, y, tileSize, tileSize);
      
      // Draw pulsing border
      const time = Date.now() / 1000;
      const pulse = Math.sin(time * 3) * 0.1 + 0.9;
      this.ctx.globalAlpha = pulse;
      this.ctx.strokeRect(x + 2, y + 2, tileSize - 4, tileSize - 4);
      this.ctx.globalAlpha = 1;
    }
  }

  private drawCityBorders(map: GameMap, camera: Camera): void {
    const cities = map.getCities();
    const tileSize = this.tileSize * camera.zoom;
    
    cities.forEach(city => {
      // Find the boundary tiles
      const boundarySegments: Array<{x1: number, y1: number, x2: number, y2: number}> = [];
      
      for (const tile of city.tiles) {
        const x = camera.x + tile.x * tileSize;
        const y = camera.y + tile.y * tileSize;
        
        // Check each edge
        const hasTop = !city.tiles.some(t => t.x === tile.x && t.y === tile.y - 1);
        const hasRight = !city.tiles.some(t => t.x === tile.x + 1 && t.y === tile.y);
        const hasBottom = !city.tiles.some(t => t.x === tile.x && t.y === tile.y + 1);
        const hasLeft = !city.tiles.some(t => t.x === tile.x - 1 && t.y === tile.y);
        
        if (hasTop) boundarySegments.push({x1: x, y1: y, x2: x + tileSize, y2: y});
        if (hasRight) boundarySegments.push({x1: x + tileSize, y1: y, x2: x + tileSize, y2: y + tileSize});
        if (hasBottom) boundarySegments.push({x1: x + tileSize, y1: y + tileSize, x2: x, y2: y + tileSize});
        if (hasLeft) boundarySegments.push({x1: x, y1: y + tileSize, x2: x, y2: y});
      }
      
      // Draw the boundary
      this.ctx.strokeStyle = city.faction === 'United States Of America' ? '#4B5320' : 
                            city.faction === '3rd Reich' ? '#708090' : 
                            city.faction === 'United Kingdom' ? '#003F87' :
                            city.faction === 'USSR' ? '#CC0000' :
                            city.faction === 'Imperial Japan' ? '#DC143C' :
                            city.faction === 'Canada' ? '#D2691E' :
                            city.faction === 'Fascist Italy' ? '#009246' :
                            city.faction === 'France' ? '#7A9BB8' :
                            city.faction === 'Greece' ? '#0072CE' :
                            '#8B7355';
      this.ctx.lineWidth = 3;
      
      boundarySegments.forEach(segment => {
        this.ctx.beginPath();
        this.ctx.moveTo(segment.x1, segment.y1);
        this.ctx.lineTo(segment.x2, segment.y2);
        this.ctx.stroke();
      });
    });
  }
  
  private drawUnits(units: Unit[], camera: Camera, selectedUnit: string | null, visionSystem?: VisionSystem, currentFaction?: FactionType): void {
    const tileSize = this.tileSize * camera.zoom;
    const animationDuration = 300; // 300ms for smooth movement
    
    
    for (const unit of units) {
      // Skip enemy units in fog of war
      if (visionSystem && currentFaction && unit.faction !== currentFaction) {
        if (!visionSystem.isPositionVisible(unit.position.x, unit.position.y)) {
          continue; // Skip drawing this unit
        }
      }
      
      // Calculate display position with animation
      let displayX = unit.position.x;
      let displayY = unit.position.y;
      
      if (unit.displayPosition && unit.targetPosition && unit.animationStartTime) {
        const elapsed = Date.now() - unit.animationStartTime;
        const progress = Math.min(elapsed / animationDuration, 1);
        
        // Smooth easing function
        const easeProgress = 1 - Math.pow(1 - progress, 3);
        
        displayX = unit.displayPosition.x + (unit.targetPosition.x - unit.displayPosition.x) * easeProgress;
        displayY = unit.displayPosition.y + (unit.targetPosition.y - unit.displayPosition.y) * easeProgress;
        
        // Update display position when animation completes
        if (progress >= 1) {
          unit.displayPosition = { ...unit.targetPosition };
          unit.targetPosition = undefined;
          unit.animationStartTime = undefined;
        }
      } else if (unit.displayPosition) {
        displayX = unit.displayPosition.x;
        displayY = unit.displayPosition.y;
      }
      
      let x = camera.x + displayX * tileSize;
      let y = camera.y + displayY * tileSize;
      let rotation = 0; // For death animation rotation
      
      // Apply death animation effects
      if (unit.deathAnimation) {
        const elapsed = Date.now() - unit.deathAnimation.startTime;
        const progress = Math.min(elapsed / unit.deathAnimation.duration, 1);
        
        if (progress < 1) {
          // Backflip animation: jump up, rotate, and fall down
          const jumpHeight = Math.sin(progress * Math.PI) * tileSize * 1.5; // Jump high
          y -= jumpHeight;
          
          // Rotate 360 degrees during the jump
          rotation = progress * Math.PI * 2;
          
          // Fade out during the last 30% of the animation
          if (progress > 0.7) {
            const fadeProgress = (progress - 0.7) / 0.3;
            this.ctx.globalAlpha = 1 - fadeProgress;
          }
        }
      }
      
      // Apply attack animation effects
      let unitColor = '#CC0000'; // Default red color
      
      // Debug faction value for Greek units
      const faction = unit.faction?.trim();
      
      if (faction === 'Greece') {
        unitColor = '#0072CE'; // Greek blue
      } else {
        switch(faction) {
          case 'United States Of America':
            unitColor = '#4B5320';
            break;
          case '3rd Reich':
            unitColor = '#708090';
            break;
          case 'United Kingdom':
            unitColor = '#003F87';
            break;
          case 'USSR':
            unitColor = '#CC0000';
            break;
          case 'Imperial Japan':
            unitColor = '#DC143C';
            break;
          case 'Canada':
            unitColor = '#D2691E';
            break;
          case 'Fascist Italy':
            unitColor = '#009246';
            break;
          case 'France':
            unitColor = '#7A9BB8';
            break;
          default:
            console.warn('[FACTION COLOR] Unknown faction:', {
              faction: faction,
              rawFaction: unit.faction,
              type: typeof unit.faction,
              unitName: unit.name,
              unitId: unit.id
            });
            break;
        }
      }
      if (unit.attackAnimation) {
        const elapsed = Date.now() - unit.attackAnimation.startTime;
        const progress = Math.min(elapsed / unit.attackAnimation.duration, 1);
        
        if (progress < 1) {
          if (unit.attackAnimation.type === 'attacker') {
            // Jump animation - sine wave for smooth jump
            const jumpHeight = Math.sin(progress * Math.PI) * 20;
            y -= jumpHeight;
            
            // Flash white
            const flashIntensity = Math.sin(progress * Math.PI * 4) * 0.5 + 0.5;
            unitColor = this.interpolateColor(unitColor, '#FFFFFF', flashIntensity);
          } else {
            // Defender animation - vibrate
            const vibrationX = (Math.random() - 0.5) * 4;
            const vibrationY = (Math.random() - 0.5) * 4;
            x += vibrationX;
            y += vibrationY;
            
            // Flash red
            const flashIntensity = Math.sin(progress * Math.PI * 4) * 0.5 + 0.5;
            unitColor = this.interpolateColor(unitColor, '#FF0000', flashIntensity);
          }
        } else {
          // Animation finished, clear it
          unit.attackAnimation = undefined;
        }
      }
      
      // Only draw if on screen
      if (x > -tileSize && x < this.canvas.width + tileSize && 
          y > -tileSize && y < this.canvas.height + tileSize) {
        
        // Draw unit base - smoothly scale based on zoom for better visibility
        // Use a smooth interpolation instead of discrete steps
        let unitSizeMultiplier = 0.8;
        if (camera.zoom < 1.0) {
          // Smoothly scale from 0.8 at zoom 1.0 to 0.95 at zoom 0.25
          unitSizeMultiplier = 0.8 + (1.0 - camera.zoom) * 0.2;
          unitSizeMultiplier = Math.min(0.95, unitSizeMultiplier);
        }
        
        const unitSize = tileSize * unitSizeMultiplier;
        const offsetX = (tileSize - unitSize) / 2;
        const offsetY = (tileSize - unitSize) / 2;
        
        // Apply grey filter for hunkered units
        const isHunkered = (unit as any).isHunkered;
        if (isHunkered) {
          this.ctx.save();
          this.ctx.filter = 'grayscale(60%) brightness(70%)';
        }
        
        // Apply rotation for death animation
        if (rotation !== 0) {
          this.ctx.save();
          this.ctx.translate(x + tileSize/2, y + tileSize/2);
          this.ctx.rotate(rotation);
          this.ctx.translate(-(x + tileSize/2), -(y + tileSize/2));
        }
        
        // Draw unit shape based on type (skip in borderless modes, but include for coloured-faction)
        if (this.spriteMode !== 'borderless' && this.spriteMode !== 'coloured-borderless') {
          // Set fill color for the unit background
          this.ctx.fillStyle = unitColor;
          this.drawUnitShape(x + tileSize/2, y + tileSize/2, unitSize/2, camera.zoom);
        }
        
        // Draw category icon in center
        if (this.unitIconsLoaded) {
          const unitDef = UNIT_DEFINITIONS[unit.type];
          const categoryIcon = this.categoryImages[unitDef.category];
          if (categoryIcon) {
            // Save current context state
            this.ctx.save();
            
            // Enable high quality image rendering
            this.ctx.imageSmoothingEnabled = true;
            this.ctx.imageSmoothingQuality = 'high';
            
            // Calculate icon size with smooth scaling based on zoom
            // At zoom 1.0 and above, use normal scaling
            // Below zoom 1.0, gradually increase relative size
            let iconSizeMultiplier;
            
            if (camera.zoom >= 1.0) {
              // Normal size when zoomed in
              iconSizeMultiplier = 0.7;
            } else {
              // Smoothly interpolate: at zoom 1.0 -> 0.7, at zoom 0.25 -> 1.0
              // This makes icons relatively larger as you zoom out
              const zoomRange = 1.0 - 0.25; // 0.75
              const currentRange = 1.0 - camera.zoom;
              const scaleRange = 1.0 - 0.7; // 0.3
              iconSizeMultiplier = 0.7 + (currentRange / zoomRange) * scaleRange;
              iconSizeMultiplier = Math.min(1.0, iconSizeMultiplier); // Cap at filling the tile
            }
            
            const iconSize = tileSize * iconSizeMultiplier;
            
            // Draw icon (with special handling for borderless and coloured-faction modes)
            if (this.spriteMode === 'borderless' || this.spriteMode === 'coloured-borderless' || this.spriteMode === 'coloured-faction') {
              // For borderless modes, we need to:
              // 1. Draw the FILLED icon colored as faction color (as a silhouette)
              // 2. Draw the OUTLINE or COLOUREDTINT icon on top for details
              this.ctx.save();
              
              // Get the filled icon for this category
              const filledIcon = this.filledCategoryImages[unitDef.category];
              if (!filledIcon) {
                // Fallback if filled icon not loaded
                this.ctx.drawImage(
                  categoryIcon,
                  x + tileSize/2 - iconSize/2,
                  y + tileSize/2 - iconSize/2,
                  iconSize,
                  iconSize
                );
                this.ctx.restore();
                continue;
              }
              
              // Get faction color
              let factionColor = '#CC0000'; // Default red
              if (unit.faction === 'United States Of America') {
                factionColor = '#4B5320'; // Olive Drab
              } else if (unit.faction === '3rd Reich') {
                factionColor = '#708090'; // Slate Gray
              } else if (unit.faction === 'United Kingdom') {
                factionColor = '#003F87'; // Royal Blue
              } else if (unit.faction === 'USSR') {
                factionColor = '#CC0000'; // Soviet Red
              } else if (unit.faction === 'Imperial Japan') {
                factionColor = '#DC143C'; // Crimson
              } else if (unit.faction === 'Canada') {
                factionColor = '#D2691E'; // Chocolate
              } else if (unit.faction === 'Fascist Italy') {
                factionColor = '#009246'; // Italian Green
              } else if (unit.faction === 'France') {
                factionColor = '#7A9BB8'; // Light Blue (Sky Blue)
              } else if (unit.faction === 'Greece') {
                factionColor = '#0072CE'; // Greek Blue
              }
              
              // Use temporary canvas for compositing
              const tempSize = Math.max(iconSize, 128);
              const tempCanvas = document.createElement('canvas');
              tempCanvas.width = tempSize;
              tempCanvas.height = tempSize;
              const tempCtx = tempCanvas.getContext('2d')!;
              tempCtx.imageSmoothingEnabled = true;
              tempCtx.imageSmoothingQuality = 'high';
              
              // Draw filled icon
              tempCtx.drawImage(filledIcon, 0, 0, tempSize, tempSize);
              
              // Color it with faction color using source-in to only affect non-transparent pixels
              tempCtx.globalCompositeOperation = 'source-in';
              tempCtx.fillStyle = factionColor;
              tempCtx.fillRect(0, 0, tempSize, tempSize);
              
              // Reset composite operation
              tempCtx.globalCompositeOperation = 'source-over';
              
              // Now draw the detail icon on top (outline for borderless, colouredtint for coloured-borderless)
              tempCtx.drawImage(categoryIcon, 0, 0, tempSize, tempSize);
              
              // Draw the composite to the main canvas
              this.ctx.drawImage(
                tempCanvas,
                x + tileSize/2 - iconSize/2,
                y + tileSize/2 - iconSize/2,
                iconSize,
                iconSize
              );
              
              this.ctx.restore();
            } else {
              // Normal icon drawing
              this.ctx.drawImage(
                categoryIcon,
                x + tileSize/2 - iconSize/2,
                y + tileSize/2 - iconSize/2,
                iconSize,
                iconSize
              );
            }
            
            // Restore context state
            this.ctx.restore();
          }
          
          // Draw level indicator in top-right corner within the unit square
          // Skip level indicators for aircraft as they have their own categories now
          const isAircraft = unitDef.category === 'fighters' || 
                           unitDef.category === 'bombers' || 
                           unitDef.category === 'transport_aircraft';
          
          if (unitDef.level > 1 && !isAircraft) {
            const levelIcon = this.levelImages[unitDef.level.toString()];
            if (levelIcon) {
              const levelIconSize = unitSize * 0.25;
              const unitLeft = x + (tileSize - unitSize) / 2;
              const unitTop = y + (tileSize - unitSize) / 2;
              this.ctx.drawImage(
                levelIcon,
                unitLeft + unitSize - levelIconSize - 3,
                unitTop + 3,
                levelIconSize,
                levelIconSize
              );
            }
          }
        }
        
        // Restore rotation context if needed
        if (rotation !== 0) {
          this.ctx.restore();
        }
        
        // Draw health text based on settings
        const healthBarMode = localStorage.getItem('healthBarMode') || 'select';
        const shouldShowHealthBar = 
          healthBarMode === 'always' || 
          (healthBarMode === 'select' && unit.id === selectedUnit);
        
        if (shouldShowHealthBar) {
          this.ctx.save();
          const healthFontSize = this.isMobile ? Math.floor(tileSize * 0.25) : Math.floor(tileSize * 0.3);
          this.ctx.font = `bold ${healthFontSize}px Arial`;
          this.ctx.textAlign = 'center';
          this.ctx.textBaseline = 'bottom';
          
          const healthText = `${unit.stats.health}/${unit.stats.maxHealth}`;
          const textMetrics = this.ctx.measureText(healthText);
          const textHeight = tileSize * 0.3;
          
          // Draw background for health text (keep it visible in all modes)
          this.ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
          this.ctx.fillRect(
            x + tileSize/2 - textMetrics.width/2 - 2,
            y + offsetY - textHeight - 2,
            textMetrics.width + 4,
            textHeight
          );
          
          // Health text color based on percentage
          const healthPercent = unit.stats.health / unit.stats.maxHealth;
          if (healthPercent > 0.6) {
            this.ctx.fillStyle = '#00FF00';
          } else if (healthPercent > 0.3) {
            this.ctx.fillStyle = '#FFFF00';
          } else {
            this.ctx.fillStyle = '#FF0000';
          }
          
          this.ctx.fillText(healthText, x + tileSize/2, y + offsetY - 2);
          this.ctx.restore();
        }
        
        // Draw selection indicator (skip in borderless modes, but include for coloured-faction)
        if (unit.id === selectedUnit && this.spriteMode !== 'borderless' && this.spriteMode !== 'coloured-borderless') {
          this.ctx.strokeStyle = '#FFD700';
          this.ctx.lineWidth = 3;
          this.ctx.strokeRect(x + offsetX - 2, y + offsetY - 2, unitSize + 4, unitSize + 4);
        }
        
        // Draw veteran star (top-left corner)
        if (unit.isVeteran) {
          this.ctx.save();
          
          // Position in top-left corner
          const starX = x + offsetX + tileSize * 0.12;
          const starY = y + offsetY + tileSize * 0.12;
          
          // Create a clean star using text (Unicode star character), smaller on mobile
          const starFontSize = this.isMobile ? Math.floor(tileSize * 0.15) : Math.floor(tileSize * 0.18);
          this.ctx.font = `bold ${starFontSize}px Arial`;
          this.ctx.textAlign = 'center';
          this.ctx.textBaseline = 'middle';
          
          // Draw black star
          this.ctx.fillStyle = 'black';
          this.ctx.fillText('★', starX, starY);
          
          this.ctx.restore();
        }
        
        
        // Draw damage text
        if (unit.damageText) {
          const elapsed = Date.now() - unit.damageText.startTime;
          const progress = Math.min(elapsed / unit.damageText.duration, 1);
          
          if (progress < 1) {
            // Calculate text position (floating upward)
            const textY = y - tileSize * 0.3 - (progress * 30); // Float up 30 pixels
            
            // Calculate opacity (fade out)
            const opacity = 1 - progress;
            
            this.ctx.save();
            const damageFontSize = this.isMobile ? Math.floor(tileSize * 0.35) : Math.floor(tileSize * 0.4);
            this.ctx.font = `bold ${damageFontSize}px Arial`;
            this.ctx.textAlign = 'center';
            this.ctx.textBaseline = 'middle';
            
            // Check if it's a critical hit or panic fire
            const isCritical = (unit.damageText as any).isCritical;
            const isPanicFire = (unit.damageText as any).isPanicFire;
            
            // Draw damage text with appropriate color and black outline
            this.ctx.globalAlpha = opacity;
            this.ctx.strokeStyle = 'black';
            this.ctx.lineWidth = 3;
            const damageText = `-${unit.damageText.amount}`;
            this.ctx.strokeText(damageText, x + tileSize/2, textY);
            
            // Use different colors: red for critical, gray for panic, yellow for normal
            if (isPanicFire) {
              this.ctx.fillStyle = '#888888'; // Gray for panic fire
            } else if (isCritical) {
              this.ctx.fillStyle = '#FF0000'; // Red for critical
            } else {
              this.ctx.fillStyle = '#FFFF00'; // Yellow for normal
            }
            this.ctx.fillText(damageText, x + tileSize/2, textY);
            
            // Draw special text above damage
            if (isCritical) {
              const criticalFontSize = this.isMobile ? Math.floor(tileSize * 0.25) : Math.floor(tileSize * 0.3);
              this.ctx.font = `bold ${criticalFontSize}px Arial`;
              this.ctx.strokeText('CRITICAL!', x + tileSize/2, textY - tileSize * 0.3);
              this.ctx.fillStyle = '#FFD700'; // Gold color for critical text
              this.ctx.fillText('CRITICAL!', x + tileSize/2, textY - tileSize * 0.3);
            } else if (isPanicFire) {
              const panicFontSize = this.isMobile ? Math.floor(tileSize * 0.2) : Math.floor(tileSize * 0.25);
              this.ctx.font = `bold ${panicFontSize}px Arial`;
              this.ctx.strokeText('PANIC!', x + tileSize/2, textY - tileSize * 0.25);
              this.ctx.fillStyle = '#FF6666'; // Light red for panic text
              this.ctx.fillText('PANIC!', x + tileSize/2, textY - tileSize * 0.25);
            }
            
            this.ctx.restore();
          } else {
            // Animation finished, clear it
            unit.damageText = undefined;
          }
        }
        
        // ALWAYS reset global alpha to prevent visual glitches
        // This must happen regardless of animation state
        this.ctx.globalAlpha = 1;
        
        // Restore context if unit was hunkered
        if (isHunkered) {
          this.ctx.restore();
        }
      }
    }
  }

  private drawCityNames(map: GameMap, camera: Camera): void {
    const cities = map.getCities();
    const tileSize = this.tileSize * camera.zoom;
    
    cities.forEach(city => {
      // Find center tile of city
      const centerIndex = Math.floor(city.tiles.length / 2);
      const centerTile = city.tiles[centerIndex];
      
      const x = camera.x + centerTile.x * tileSize + tileSize / 2;
      const y = camera.y + centerTile.y * tileSize - tileSize * 0.2; // Position above the city
      
      // Only draw if on screen
      if (x > -100 && x < this.canvas.width + 100 && y > -50 && y < this.canvas.height + 50) {
        const cityLevel = city.level || CityLevel.OUTPOST;
        
        // Scale font with zoom, adjust for mobile
        const baseFontSize = this.isMobile ? 14 : 16;
        const minFontSize = this.isMobile ? 10 : 12;
        const fontSize = Math.max(minFontSize, Math.floor(baseFontSize * camera.zoom));
        
        // Draw city name
        this.ctx.font = `bold ${fontSize}px Arial`;
        this.ctx.textAlign = 'center';
        
        // Get display name with capture status
        let displayName = city.captureProgress ? `${city.name} - Capturing` : city.name;
        
        // Add level indicator (squared/cubed) to the name
        if (cityLevel === CityLevel.FACTORY) {
          displayName += '²'; // Squared symbol for level 2
        } else if (cityLevel === CityLevel.AIRBASE) {
          displayName += '³'; // Cubed symbol for level 3
        }
        // No symbol for level 1 (Outpost)
        
        // Measure text for background
        const textWidth = this.ctx.measureText(displayName).width;
        const textHeight = fontSize;
        
        // Draw black background/border for text
        this.ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
        this.ctx.fillRect(x - textWidth/2 - 5, y - textHeight + 2, textWidth + 10, textHeight + 2);
        
        // Draw text with black outline
        this.ctx.strokeStyle = 'black';
        this.ctx.lineWidth = 2;
        this.ctx.strokeText(displayName, x, y);
        this.ctx.fillStyle = 'white';
        this.ctx.fillText(displayName, x, y);
        
        // Draw upgrading icon if upgrading
        if (city.upgradeInProgress) {
          const upgradingIcon = this.categoryImages['upgrading'];
          if (upgradingIcon) {
            // Scale icon with the font size to keep consistent positioning
            const iconSize = fontSize * 3.0; // Double the icon size (was 1.5)
            const iconY = y - fontSize * 4.0; // Move much higher to float above the black border
            
            // Apply special rendering for borderless and coloured-faction modes
            if (this.spriteMode === 'borderless' || this.spriteMode === 'coloured-faction') {
              // For borderless mode, draw filled color BEHIND the outline icon
              this.ctx.save();
              
              // Use a higher resolution for quality
              const tempSize = Math.max(iconSize, 128);
              const tempCanvas = document.createElement('canvas');
              tempCanvas.width = tempSize;
              tempCanvas.height = tempSize;
              const tempCtx = tempCanvas.getContext('2d')!;
              
              // Enable image smoothing
              tempCtx.imageSmoothingEnabled = true;
              tempCtx.imageSmoothingQuality = 'high';
              
              // Get city faction color
              let factionColor = '#CC0000';
              if (city.faction === 'United States Of America') {
                factionColor = '#4B5320';
              } else if (city.faction === '3rd Reich') {
                factionColor = '#708090';
              } else if (city.faction === 'United Kingdom') {
                factionColor = '#003F87';
              } else if (city.faction === 'USSR') {
                factionColor = '#CC0000';
              } else if (city.faction === 'Imperial Japan') {
                factionColor = '#DC143C';
              } else if (city.faction === 'Canada') {
                factionColor = '#D2691E';
              } else if (city.faction === 'Fascist Italy') {
                factionColor = '#009246';
              } else if (city.faction === 'France') {
                factionColor = '#7A9BB8';
              } else if (city.faction === 'Greece') {
                factionColor = '#0072CE';
              }
              
              // Fill with faction color at 100% opacity
              tempCtx.fillStyle = factionColor;
              tempCtx.fillRect(0, 0, tempSize, tempSize);
              
              // Draw the outline icon on top
              tempCtx.drawImage(upgradingIcon, 0, 0, tempSize, tempSize);
              
              // Draw the composite
              this.ctx.drawImage(
                tempCanvas,
                x - iconSize / 2,
                iconY,
                iconSize,
                iconSize
              );
              
              this.ctx.restore();
            } else if (this.spriteMode === 'coloured-borderless') {
              // For coloured-borderless mode, use same approach as borderless
              // Get the filled upgrading icon
              const filledUpgradingIcon = this.filledCategoryImages['upgrading'];
              
              if (filledUpgradingIcon) {
                this.ctx.save();
                
                // Use a higher resolution for quality
                const tempSize = Math.max(iconSize, 128);
                const tempCanvas = document.createElement('canvas');
                tempCanvas.width = tempSize;
                tempCanvas.height = tempSize;
                const tempCtx = tempCanvas.getContext('2d')!;
                
                // Enable image smoothing
                tempCtx.imageSmoothingEnabled = true;
                tempCtx.imageSmoothingQuality = 'high';
                
                // Get city faction color
                let factionColor = '#CC0000';
                if (city.faction === 'United States Of America') {
                  factionColor = '#4B5320';
                } else if (city.faction === '3rd Reich') {
                  factionColor = '#708090';
                } else if (city.faction === 'United Kingdom') {
                  factionColor = '#003F87';
                } else if (city.faction === 'USSR') {
                  factionColor = '#CC0000';
                } else if (city.faction === 'Imperial Japan') {
                  factionColor = '#DC143C';
                } else if (city.faction === 'Canada') {
                  factionColor = '#D2691E';
                } else if (city.faction === 'Fascist Italy') {
                  factionColor = '#009246';
                } else if (city.faction === 'France') {
                  factionColor = '#7A9BB8';
                } else if (city.faction === 'Greece') {
                  factionColor = '#0072CE';
                }
                
                // Draw filled icon
                tempCtx.drawImage(filledUpgradingIcon, 0, 0, tempSize, tempSize);
                
                // Color it with faction color
                tempCtx.globalCompositeOperation = 'source-in';
                tempCtx.fillStyle = factionColor;
                tempCtx.fillRect(0, 0, tempSize, tempSize);
                
                // Reset and draw ColouredTint icon on top
                tempCtx.globalCompositeOperation = 'source-over';
                tempCtx.drawImage(upgradingIcon, 0, 0, tempSize, tempSize);
                
                // Draw the composite
                this.ctx.drawImage(
                  tempCanvas,
                  x - iconSize / 2,
                  iconY,
                  iconSize,
                  iconSize
                );
                
                this.ctx.restore();
              } else {
                // Fallback
                this.ctx.drawImage(
                  upgradingIcon,
                  x - iconSize / 2,
                  iconY,
                  iconSize,
                  iconSize
                );
              }
            } else {
              // Normal icon drawing
              this.ctx.drawImage(
                upgradingIcon,
                x - iconSize / 2,
                iconY,
                iconSize,
                iconSize
              );
            }
          }
        }
      }
    });
  }


  private drawTileOutline(x: number, y: number, zoom: number): void {
    const size = this.tileSize * zoom;
    
    this.ctx.strokeStyle = 'rgba(0, 0, 0, 0.3)';
    this.ctx.lineWidth = 1;
    this.ctx.strokeRect(x, y, size, size);
  }

  private drawTileHighlight(x: number, y: number, zoom: number): void {
    const size = this.tileSize * zoom;
    
    this.ctx.strokeStyle = '#FFD700';
    this.ctx.lineWidth = 3;
    this.ctx.strokeRect(x + 1, y + 1, size - 2, size - 2);
  }

  screenToTile(screenX: number, screenY: number, camera: Camera): Position {
    const tileSize = this.tileSize * camera.zoom;
    return {
      x: Math.floor((screenX - camera.x) / tileSize),
      y: Math.floor((screenY - camera.y) / tileSize)
    };
  }

  private drawUnitShape(centerX: number, centerY: number, radius: number, _zoom: number = 1): void {
    this.ctx.save();
    this.ctx.beginPath();
    
    // Draw rounded rectangle for unit shape
    const x = centerX - radius;
    const y = centerY - radius;
    const width = radius * 2;
    const height = radius * 2;
    const cornerRadius = radius * 0.15; // 15% of the radius for corner rounding
    
    // Create rounded rectangle path
    this.ctx.moveTo(x + cornerRadius, y);
    this.ctx.lineTo(x + width - cornerRadius, y);
    this.ctx.quadraticCurveTo(x + width, y, x + width, y + cornerRadius);
    this.ctx.lineTo(x + width, y + height - cornerRadius);
    this.ctx.quadraticCurveTo(x + width, y + height, x + width - cornerRadius, y + height);
    this.ctx.lineTo(x + cornerRadius, y + height);
    this.ctx.quadraticCurveTo(x, y + height, x, y + height - cornerRadius);
    this.ctx.lineTo(x, y + cornerRadius);
    this.ctx.quadraticCurveTo(x, y, x + cornerRadius, y);
    this.ctx.closePath();
    
    // Fill the background
    this.ctx.fill();
    
    // Draw border
    this.ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)';
    this.ctx.lineWidth = 2;
    this.ctx.stroke();
    
    this.ctx.restore();
  }

  
  private interpolateColor(color1: string, color2: string, factor: number): string {
    // Convert hex to RGB
    const c1 = this.hexToRgb(color1);
    const c2 = this.hexToRgb(color2);
    
    const r = Math.round(c1.r + (c2.r - c1.r) * factor);
    const g = Math.round(c1.g + (c2.g - c1.g) * factor);
    const b = Math.round(c1.b + (c2.b - c1.b) * factor);
    
    return `rgb(${r}, ${g}, ${b})`;
  }
  
  private hexToRgb(hex: string): { r: number, g: number, b: number } {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 0, g: 0, b: 0 };
  }
  
  private drawFogOfWar(visibleTiles: Array<{tile: Tile, screenX: number, screenY: number}>, visionSystem: VisionSystem, camera: Camera): void {
    const tileSize = this.tileSize * camera.zoom;
    
    // Create a gradient for smooth fog edges
    this.ctx.save();
    
    for (const { tile, screenX, screenY } of visibleTiles) {
      if (!visionSystem.isPositionVisible(tile.x, tile.y)) {
        // Apply fog overlay with 50% darkness
        this.ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
        this.ctx.fillRect(screenX, screenY, tileSize, tileSize);
        
        // Add smooth edges by checking neighboring tiles
        const neighbors = [
          { dx: -1, dy: 0 }, { dx: 1, dy: 0 },
          { dx: 0, dy: -1 }, { dx: 0, dy: 1 },
          { dx: -1, dy: -1 }, { dx: 1, dy: -1 },
          { dx: -1, dy: 1 }, { dx: 1, dy: 1 }
        ];
        
        let visibleNeighbors = 0;
        for (const { dx, dy } of neighbors) {
          if (visionSystem.isPositionVisible(tile.x + dx, tile.y + dy)) {
            visibleNeighbors++;
          }
        }
        
        // If this fog tile has visible neighbors, draw a softer edge
        if (visibleNeighbors > 0) {
          const gradient = this.ctx.createRadialGradient(
            screenX + tileSize/2, screenY + tileSize/2, 0,
            screenX + tileSize/2, screenY + tileSize/2, tileSize * 0.7
          );
          gradient.addColorStop(0, 'rgba(0, 0, 0, 0.3)');
          gradient.addColorStop(1, 'rgba(0, 0, 0, 0.5)');
          this.ctx.fillStyle = gradient;
          this.ctx.fillRect(screenX, screenY, tileSize, tileSize);
        }
      }
    }
    
    this.ctx.restore();
  }
  
  private async loadMineIcon(): Promise<void> {
    return new Promise<void>((resolve) => {
      // Use sprite mode for mines
      const getSpritePath = (baseName: string) => {
        if (this.spriteMode === 'filled') return `/NEWIcons/${baseName}Filled.png`;
        if (this.spriteMode === 'coloured') return `/NEWIcons/${baseName}Coloured.png`;
        if (this.spriteMode === 'silhouette') return `/NEWIcons/${baseName}Silhouette.png`;
        if (this.spriteMode === 'borderless') return `/NEWIcons/${baseName}.png`; // Use outline icons
        if (this.spriteMode === 'coloured-borderless') return `/NEWIcons/${baseName}ColouredTint.png`;
        if (this.spriteMode === 'coloured-faction') return `/NEWIcons/${baseName}ColouredTint.png`;
        return `/NEWIcons/${baseName}.png`;
      };
      
      // Load the current sprite mode version
      const img = new Image();
      const minePath = getSpritePath('Mine');
      
      // Also load filled version for borderless modes
      const filledImg = new Image();
      const filledPath = `/NEWIcons/MineFilled.png`;
      
      let mainLoaded = false;
      let filledLoaded = false;
      
      const checkComplete = () => {
        if (mainLoaded && filledLoaded) {
          resolve();
        }
      };
      
      img.onload = () => {
        this.mineImage = img;
        debug.log(`Mine icon loaded: ${minePath}`);
        mainLoaded = true;
        checkComplete();
      };
      img.onerror = () => {
        debug.warn(`Failed to load mine icon: ${minePath}`);
        mainLoaded = true;
        checkComplete();
      };
      
      filledImg.onload = () => {
        this.filledMineImage = filledImg;
        debug.log(`Filled mine icon loaded: ${filledPath}`);
        filledLoaded = true;
        checkComplete();
      };
      filledImg.onerror = () => {
        debug.warn(`Failed to load filled mine icon: ${filledPath}`);
        filledLoaded = true;
        checkComplete();
      };
      
      // Add cache-busting timestamp to ensure fresh load
      img.src = `${minePath}?t=${Date.now()}`;
      filledImg.src = `${filledPath}?t=${Date.now()}`;
    });
  }
  
  private drawMinePlacementTiles(tiles: Position[], camera: Camera): void {
    const tileSize = this.tileSize * camera.zoom;
    
    this.ctx.save();
    
    for (const pos of tiles) {
      const x = camera.x + pos.x * tileSize;
      const y = camera.y + pos.y * tileSize;
      
      // Draw yellow highlight for mine placement
      this.ctx.fillStyle = 'rgba(255, 255, 0, 0.3)';
      this.ctx.fillRect(x, y, tileSize, tileSize);
      
      // Draw yellow border
      this.ctx.strokeStyle = '#ffff00';
      this.ctx.lineWidth = 2;
      this.ctx.strokeRect(x + 1, y + 1, tileSize - 2, tileSize - 2);
    }
    
    this.ctx.restore();
  }
  
  private drawHealTargetTiles(tiles: Position[], camera: Camera): void {
    const tileSize = this.tileSize * camera.zoom;
    
    this.ctx.save();
    
    for (const pos of tiles) {
      const x = camera.x + pos.x * tileSize;
      const y = camera.y + pos.y * tileSize;
      
      // Draw green highlight for heal/repair targets
      this.ctx.fillStyle = 'rgba(0, 255, 0, 0.3)';
      this.ctx.fillRect(x, y, tileSize, tileSize);
      
      // Draw green border
      this.ctx.strokeStyle = '#00ff00';
      this.ctx.lineWidth = 2;
      this.ctx.strokeRect(x + 1, y + 1, tileSize - 2, tileSize - 2);
    }
    
    this.ctx.restore();
  }
  
  private drawBoardingTargetTiles(tiles: Position[], camera: Camera): void {
    const tileSize = this.tileSize * camera.zoom;
    
    this.ctx.save();
    
    for (const pos of tiles) {
      const x = camera.x + pos.x * tileSize;
      const y = camera.y + pos.y * tileSize;
      
      // Draw blue highlight for boarding targets
      this.ctx.fillStyle = 'rgba(0, 100, 255, 0.3)';
      this.ctx.fillRect(x, y, tileSize, tileSize);
      
      // Draw blue border
      this.ctx.strokeStyle = '#0064ff';
      this.ctx.lineWidth = 2;
      this.ctx.strokeRect(x + 1, y + 1, tileSize - 2, tileSize - 2);
    }
    
    this.ctx.restore();
  }
  
  private drawMines(map: GameMap, camera: Camera, currentFaction?: FactionType, visionSystem?: VisionSystem): void {
    if (!this.mineImage) return;
    
    const tileSize = this.tileSize * camera.zoom;
    
    // Get all visible tiles
    const startX = Math.floor(-camera.x / tileSize);
    const startY = Math.floor(-camera.y / tileSize);
    const endX = Math.ceil((this.canvas.width - camera.x) / tileSize);
    const endY = Math.ceil((this.canvas.height - camera.y) / tileSize);
    
    for (let y = startY; y < endY; y++) {
      for (let x = startX; x < endX; x++) {
        const tile = map.getTile(x, y);
        if (!tile || !tile.mine) continue;
        
        // Only draw mines that belong to the current faction
        // This makes mines invisible to enemies
        if (tile.mine.owner !== currentFaction) continue;
        
        // Check if tile is visible (if fog of war is enabled)
        if (visionSystem && !visionSystem.isPositionVisible(x, y)) continue;
        
        // Draw the mine icon
        const screenX = camera.x + x * tileSize;
        const screenY = camera.y + y * tileSize;
        
        // Use same scaling logic as units for consistent zoom behavior
        let iconSizeMultiplier;
        
        if (camera.zoom >= 1.0) {
          // Normal size when zoomed in
          iconSizeMultiplier = 0.7;
        } else {
          // Smoothly interpolate: at zoom 1.0 -> 0.7, at zoom 0.25 -> 1.0
          const zoomRange = 1.0 - 0.25; // 0.75
          const currentRange = 1.0 - camera.zoom;
          const scaleRange = 1.0 - 0.7; // 0.3
          iconSizeMultiplier = 0.7 + (currentRange / zoomRange) * scaleRange;
          iconSizeMultiplier = Math.min(1.0, iconSizeMultiplier); // Cap at filling the tile
        }
        
        const mineSize = tileSize * iconSizeMultiplier;
        const offset = (tileSize - mineSize) / 2;
        
        // Enable image smoothing for better quality
        this.ctx.imageSmoothingEnabled = true;
        this.ctx.imageSmoothingQuality = 'high';
        
        // Apply special rendering for borderless and coloured-faction modes
        if ((this.spriteMode === 'borderless' || this.spriteMode === 'coloured-borderless' || this.spriteMode === 'coloured-faction') && currentFaction && this.filledMineImage) {
          this.ctx.save();
          
          // Use a higher resolution for quality
          const tempSize = Math.max(mineSize, 128);
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = tempSize;
          tempCanvas.height = tempSize;
          const tempCtx = tempCanvas.getContext('2d')!;
          
          // Enable image smoothing
          tempCtx.imageSmoothingEnabled = true;
          tempCtx.imageSmoothingQuality = 'high';
          
          // Draw the filled mine icon to temp canvas
          tempCtx.drawImage(this.filledMineImage, 0, 0, tempSize, tempSize);
          
          // Get faction color for the mine owner
          let factionColor = '#CC0000';
          if (currentFaction === 'United States Of America') {
            factionColor = '#4B5320';
          } else if (currentFaction === '3rd Reich') {
            factionColor = '#708090';
          } else if (currentFaction === 'United Kingdom') {
            factionColor = '#003F87';
          } else if (currentFaction === 'USSR') {
            factionColor = '#CC0000';
          } else if (currentFaction === 'Imperial Japan') {
            factionColor = '#DC143C';
          } else if (currentFaction === 'Canada') {
            factionColor = '#D2691E';
          } else if (currentFaction === 'Fascist Italy') {
            factionColor = '#009246';
          } else if (currentFaction === 'France') {
            factionColor = '#7A9BB8';
          } else if (currentFaction === 'Greece') {
            factionColor = '#0072CE';
          }
          
          // Color it with faction color using source-in to only affect non-transparent pixels
          tempCtx.globalCompositeOperation = 'source-in';
          tempCtx.fillStyle = factionColor;
          tempCtx.fillRect(0, 0, tempSize, tempSize);
          
          // Reset composite operation and draw the detail icon on top
          tempCtx.globalCompositeOperation = 'source-over';
          tempCtx.drawImage(this.mineImage, 0, 0, tempSize, tempSize);
          
          // Draw the composite mine
          this.ctx.drawImage(
            tempCanvas,
            screenX + offset,
            screenY + offset,
            mineSize,
            mineSize
          );
          
          this.ctx.restore();
        } else {
          // Normal mine drawing
          this.ctx.drawImage(
            this.mineImage,
            screenX + offset,
            screenY + offset,
            mineSize,
            mineSize
          );
        }
      }
    }
  }
}